import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  BroadcastFilters,
  BroadcastPreview,
  BroadcastSettings,
  BroadcastVariableMapping,
  WabaTemplateMedia,
  WhatsappBroadcast,
  WhatsappBroadcastRecipient,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";
import { useCrmStore } from "@/stores/crm.store";

const BASE = "/crm/whatsapp/broadcasts";

/**
 * The filter set a broadcast is built from.
 *
 * Read straight from the store, the way `useLeads` does — NEVER from anything
 * `usePipeline` computed. The Kanban board deliberately omits `stage` from its
 * own query and hides columns client-side instead, so a broadcast that inherited
 * the board's filter object would message the entire pipeline when the advisor
 * had narrowed it to some columns. `stages` below is the whole point of this
 * hook existing separately.
 */
export function useBroadcastFilters(): BroadcastFilters {
  const stages = useCrmStore((s) => s.stages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  return {
    ...(stages.length > 0 ? { stages } : {}),
    ...(channel ? { channel: channel as BroadcastFilters["channel"] } : {}),
    ...(scoreMin !== "" ? { scoreMin: Number(scoreMin) } : {}),
    ...(scoreMax !== "" ? { scoreMax: Number(scoreMax) } : {}),
    ...(productIds.length > 0 ? { productIds } : {}),
    ...(cycleIds.length > 0 ? { cycleIds } : {}),
    ...(search ? { search } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(labelIds.length > 0 ? { labelIds } : {}),
    ...(assigneeIds.length > 0 ? { assigneeIds } : {}),
  };
}

/** Plan + the school's authorization + transport, answered server-side. */
export function useBroadcastSettings() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["broadcast-settings", clientId],
    queryFn: () => api.get<BroadcastSettings>(`${BASE}/settings`),
    enabled: !!clientId,
    staleTime: 60_000,
    // A 403 here is the answer ("not authorized"), not a transient failure.
    retry: false,
  });
}

export function useSetBroadcastAuthorization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.put<BroadcastSettings>(`${BASE}/settings`, { enabled }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["broadcast-settings"] });
      toast.success(
        data.enabled
          ? "Los envíos masivos quedaron habilitados para el equipo."
          : "Los envíos masivos quedaron deshabilitados.",
      );
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * Who would receive this, and what it would cost.
 *
 * POST despite being a read: the filter set is a structured object (label
 * arrays, a variable mapping) that a querystring would flatten — and the
 * `stage` field is exactly the kind of thing a lossy encoding loses.
 */
export function useBroadcastPreview(opts: {
  filters: BroadcastFilters;
  templateId?: string;
  mapping?: BroadcastVariableMapping;
  enabled?: boolean;
}) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: [
      "broadcast-preview",
      clientId,
      opts.filters,
      opts.templateId ?? null,
      opts.mapping ?? null,
    ],
    queryFn: () =>
      api.post<BroadcastPreview>(`${BASE}/recipients/preview`, {
        filters: opts.filters,
        ...(opts.templateId ? { templateId: opts.templateId } : {}),
        ...(opts.mapping ? { mapping: opts.mapping } : {}),
      }),
    enabled: !!clientId && opts.enabled !== false,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * What this template needs before it can be sent: its body variables, and
 * whether its header needs a file.
 *
 * The header comes back as DATA and not as a rejection, because this is the read
 * step 1 gates on and it has to be able to render a picker. `launch` is where a
 * missing file becomes a refusal.
 */
export type BroadcastTemplateRequirements = {
  variables: string[];
  header: {
    format: string;
    requiresMedia: boolean;
    /** The template's own file, offered as this send's default. */
    defaultMedia: WabaTemplateMedia | null;
  };
};

export function useTemplateVariables(templateId: string | null) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["broadcast-template-variables", clientId, templateId],
    queryFn: () =>
      api.get<BroadcastTemplateRequirements>(
        `${BASE}/templates/${templateId ?? ""}/variables`,
      ),
    enabled: !!clientId && !!templateId,
    staleTime: 0,
    retry: false,
  });
}

export function useCreateBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      templateId: string;
      filters: BroadcastFilters;
      mapping: BroadcastVariableMapping;
      /** The file for this send; null on a text-header template. */
      headerMediaAssetId: string | null;
      /** What the recipient sees on a PDF; null unless the header is a document. */
      headerMediaFileName: string | null;
    }) => api.post<WhatsappBroadcast>(BASE, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["broadcasts"] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useBroadcasts(page = 1, pageSize = 20) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["broadcasts", clientId, page, pageSize],
    queryFn: () =>
      api.get<{ items: WhatsappBroadcast[]; total: number }>(
        `${BASE}?page=${String(page)}&pageSize=${String(pageSize)}`,
      ),
    enabled: !!clientId,
    // Poll only while something is actually running, so a quiet screen is a
    // quiet network.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(
        (b) => b.status === "queued" || b.status === "sending",
      )
        ? 5_000
        : false,
  });
}

export function useBroadcast(id: string | undefined) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["broadcasts", clientId, id],
    queryFn: () => api.get<WhatsappBroadcast>(`${BASE}/${id ?? ""}`),
    enabled: !!clientId && !!id,
    refetchInterval: (query) =>
      query.state.data?.status === "queued" || query.state.data?.status === "sending"
        ? 5_000
        : false,
  });
}

export function useBroadcastRecipients(
  id: string | undefined,
  status: string,
  page = 1,
) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["broadcast-recipients", clientId, id, status, page],
    queryFn: () =>
      api.get<{ items: WhatsappBroadcastRecipient[]; total: number }>(
        `${BASE}/${id ?? ""}/recipients?page=${String(page)}&pageSize=50${
          status ? `&status=${status}` : ""
        }`,
      ),
    enabled: !!clientId && !!id,
  });
}

export function useCancelBroadcast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`${BASE}/${id}/cancel`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["broadcasts"] });
      void queryClient.invalidateQueries({ queryKey: ["broadcast-recipients"] });
      toast.success("Envío detenido. Los mensajes ya enviados no se pueden retirar.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
